<svelte:options runes={true} />

<script lang="ts">
  import type { Snippet } from "svelte";
  import type { HTMLInputAttributes } from "svelte/elements";
  import IconSearch from "~icons/dusk/search";
  import IconAlertCircle from "~icons/dusk/alert-circle";

  export type InputVariant = "default" | "search";

  type OwnProps = {
    variant?: InputVariant;
    leadingIcon?: Snippet;
    destructive?: boolean;
    class?: string;
    value?: string;
  };

  type Props = OwnProps & Omit<HTMLInputAttributes, "class" | "value">;

  let {
    variant = "default",
    leadingIcon,
    destructive = false,
    class: className = "",
    value = $bindable(""),
    disabled,
    type = variant === "search" ? "search" : "text",
    role = variant === "search" ? "searchbox" : undefined,
    ...restProps
  }: Props = $props();

  const wrapperClasses = $derived(
    [
      "da-input",
      `da-input--${variant}`,
      destructive && "da-input--destructive",
      disabled && "da-input--disabled",
      className,
    ]
      .filter(Boolean)
      .join(" "),
  );
</script>

<div class={wrapperClasses}>
  {#if leadingIcon && variant === "default"}
    <span class="da-input__leading-icon" aria-hidden="true">
      {@render leadingIcon()}
    </span>
  {/if}

  <input
    class="da-input__field"
    {type}
    {role}
    bind:value
    aria-invalid={destructive || undefined}
    {disabled}
    {...restProps}
  />

  {#if destructive}
    <span class="da-input__trailing-icon da-input__trailing-icon--error" aria-hidden="true">
      <IconAlertCircle width="16" height="16" />
    </span>
  {:else if variant === "search"}
    <span class="da-input__trailing-icon" aria-hidden="true">
      <IconSearch width="20" height="20" />
    </span>
  {/if}
</div>

<style>
  @layer aurora {
    .da-input {
      --_border-color: var(--da-color-charcoal-50);
      --_shadow: none;
      --_focus-shadow: 0px 1px 2px rgba(10, 13, 18, 0.05), 0px 0px 0px 2px #fbfbfb;

      position: relative;
      display: flex;
      align-items: center;
      gap: var(--da-space-2);
      padding: 10px 14px;
      background: var(--da-color-white);
      border: 1px solid var(--_border-color);
      border-radius: var(--da-radius-xl);
      box-shadow: var(--_shadow);
      transition:
        border-color var(--da-duration-normal) var(--da-ease-out),
        box-shadow var(--da-duration-normal) var(--da-ease-out);
    }

    .da-input--search {
      --_shadow: var(--da-shadow-xs);
    }

    .da-input--destructive {
      --_border-color: var(--da-color-red-300);
      --_shadow: var(--da-shadow-xs);
    }

    .da-input--disabled {
      opacity: 0.4;
      cursor: not-allowed;
      pointer-events: none;
    }

    .da-input:hover:not(.da-input--disabled):not(.da-input--destructive):not(:focus-within) {
      --_border-color: var(--da-color-charcoal-200);
    }

    .da-input:focus-within:not(.da-input--disabled) {
      --_border-color: var(--da-color-charcoal-300);
      box-shadow: var(--_focus-shadow);
    }

    .da-input__field {
      flex: 1 0 0;
      min-width: 0;
      background: transparent;
      box-shadow: none !important;
      border: none !important;
      outline: none !important;
      font-family: var(--da-font-family-primary);
      font-size: var(--da-font-size-md);
      font-weight: var(--da-font-weight-regular);
      line-height: var(--da-line-height-md);
      color: var(--da-color-charcoal-500);
      padding: 0;
    }

    .da-input__field::placeholder {
      color: var(--da-color-charcoal-300);
    }

    .da-input:focus-within .da-input__field {
      color: var(--da-color-charcoal-900);
    }

    .da-input__leading-icon {
      display: flex;
      align-items: center;
      flex-shrink: 0;
      color: var(--da-color-charcoal-300);
    }

    .da-input__trailing-icon {
      display: flex;
      align-items: center;
      flex-shrink: 0;
      color: var(--da-color-charcoal-300);
    }

    .da-input__trailing-icon--error {
      color: var(--da-color-red-500);
    }

    :global(.da-input--search .da-input__field::-webkit-search-cancel-button) {
      display: none;
    }
  }
</style>
